import AButton from '@/components/A/AButton/AButton'
import styles from './AccountPage.module.less'
import cls from 'classnames'
import { Divider, Spin, message } from 'antd'
import AIcon from '@/components/A/AIcon/AIcon'
import { useEffect, useState } from 'react'
import NiceModal from '@ebay/nice-modal-react'
import AddMemberToWorkspaceModal from '@/components/Modals/AddMemberToWorkspaceModal/AddMemberToWorkspaceModal'
import CommonModal from '@/components/Modals/CommonModal/CommonModal'
import DefaultAvatar from '@/components/DefaultAvatar/DefaultAvatar'
import { observer } from 'mobx-react'
import { userService } from '@/services/userService'
import { api } from '@/api'
import ChangeAccountNameModal from '@/components/Modals/ChangeAccountNameModal/ChangeAccountNameModal'
import ChangeAccountPasswordModal from '@/components/Modals/ChangeAccountPasswordModal/ChangeAccountPasswordModal'
import { usePagination, useRequest } from 'alova/client'
import APagination from '@/components/A/APagination/APagination'
import ASwitch from '@/components/A/ASwitch/ASwitch'
import { SwitchChangeEventHandler } from 'antd/es/switch'
import { useNavigate } from 'react-router-dom'
import { PAGE_LINKS } from '@/router/pageConst'
import { useTranslation } from 'react-i18next'
import MiddleText from '@/components/MiddleText/MiddleText'
import ChangeCorpNameModal from '@/components/Modals/ChangeCorpNameModal/ChangeCorpNameModal'
import CheckInvitationCodeModal from '@/components/Modals/CheckInvitationCodeModal/CheckInvitationCodeModal'

function AccountPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [hasRequest, setHasRequest] = useState(false)

  const {
    loading,
    data: memberList,
    page,
    pageSize,
    total,
    update,
    refresh,
  } = usePagination((pageNo, pageSize) => api.staff.memberList({ pageNo, pageSize }), {
    initialData: { total: 0, data: {} },
    initialPage: 1,
    initialPageSize: 20,
    total: (res) => res.totalCount,
    data: (res) => res.objects,
  })

  /** 这里不会和上面的 usePagination 发起两次请求，这里请求会走 alova 的缓存 */
  const { data, send } = useRequest(api.staff.memberList({ pageNo: page, pageSize: pageSize }))

  useEffect(() => {
    if (!loading && page === 1) {
      const hasRequest = memberList.some((member) => member.status === 'applying')
      setHasRequest(hasRequest)
    }
  }, [loading, page, memberList])

  useEffect(() => {
    if (userService.userInfo) {
      setUsername(userService.userInfo.name)
      setEmail(userService.userInfo.email)
    }
  }, [userService.userInfo])

  const showAddMemberToWorkspaceModal = () => {
    if (data?.seatInfo.boundCount >= data?.seatInfo.baseSeatCount) {
      NiceModal.show(CommonModal, {
        content: t('dashboard.accountPage.message.yourTeamIsFull'),
        cancelText: t('dashboard.accountPage.message.ok'),
        confirmText: t('dashboard.accountPage.message.contactUs'),
        onConfirm: () => {
          navigate(PAGE_LINKS.contact)
        },
      })
      return
    }
    NiceModal.show(AddMemberToWorkspaceModal, {
      boundCount: data?.seatInfo.boundCount,
      baseSeatCount: data?.seatInfo.baseSeatCount,
      onConfirm: async () => {
        message.success(t('dashboard.accountPage.message.invitationLinkSent'))
      },
      contactUs: () => {
        navigate(PAGE_LINKS.contact)
      },
    })
  }
  const showRemoveMemberModal = (member: MemberModels.MemberItem) => {
    NiceModal.show(CommonModal, {
      title: t('dashboard.accountPage.message.removeReminder'),
      confirmText: t('dashboard.accountPage.message.remove'),
      cancelText: t('dashboard.accountPage.message.cancel'),
      content: t('dashboard.accountPage.message.removeMember'),
      onConfirm: async () => {
        try {
          await api.staff
            .remove(member.id)
            .send()
            .then(() => {
              refresh(page)
              send()
            })
          message.success(t('dashboard.accountPage.message.removeMemberSuccess'))
        } catch (error) {
          const e = error as ApiError
          if (e.statusCode === 40001) {
            message.error(t('dashboard.accountPage.message.notAdminOperate'))
          } else {
            message.error(e.statusMessage)
          }
        }
      },
    })
  }
  const handleChangeUsername = () => {
    NiceModal.show(ChangeAccountNameModal, {
      username: username,
      onConfirm: (username) => {
        userService.refreshUserInfo()
        setUsername(username)
      },
    })
  }
  const handleChangePassword = () => {
    NiceModal.show(ChangeAccountPasswordModal, {
      hasPassword: userService.userInfo?.hasPassword,
      onConfirm: () => {
        userService.refreshUserInfo()
      },
    })
  }

  const handleProcessStaffRequest = async (
    member: MemberModels.MemberItem,
    status: 'deny' | 'approve'
  ) => {
    if (data?.seatInfo.boundCount >= data?.seatInfo.baseSeatCount) {
      NiceModal.show(CommonModal, {
        content: t('dashboard.accountPage.message.yourTeamIsFull'),
        cancelText: t('dashboard.accountPage.message.ok'),
        confirmText: t('dashboard.accountPage.message.contactUs'),
        onConfirm: () => {
          navigate(PAGE_LINKS.contact)
        },
      })
      return
    }
    await api.staff.processStaffRequest({ staffId: member.id, status }).send()
    message.success(t('dashboard.accountPage.message.applicationProcessedSuccessfully'))
    refresh()
  }

  const handlePageChange = (page: number) => {
    update({ page })
  }

  const handleNotificationChange: SwitchChangeEventHandler = (checked) => {
    api.staff
      .updateNotification(checked)
      .send(true)
      .then(() => {
        const msg = checked
          ? t('dashboard.accountPage.message.notificationSettingsTurnedOn')
          : t('dashboard.accountPage.message.notificationSettingsTurnedOff')
        message.success(msg)
        userService.refreshUserInfo()
      })
  }

  const handleChangeCorpName = () => {
    NiceModal.show(ChangeCorpNameModal, {
      corpName: userService.userInfo?.corpName,
      onConfirm: async (corpName) => {
        try {
          await api.corp.changeCorpName({ corpName }).send(true)
          userService.refreshUserInfo()
          message.success(t('dashboard.accountPage.message.changeCorpNameSuccess'))
        } catch (e) {
          const error = e as ApiError
          if (error.statusCode === 40001) {
            message.error(t('dashboard.accountPage.message.noPermission'))
          } else {
            message.error(t('dashboard.accountPage.message.changeCorpNameFailed'))
          }
        }
      },
    })
  }

  const handleCheckInvitationCode = () => {
    NiceModal.show(CheckInvitationCodeModal)
  }

  return (
    <div className={cls(styles.accountPage)} data-testid='account-page'>
      <div className={styles.accountPageContent}>
        <div className={styles.userInfo}>
          <DefaultAvatar
            width={54}
            height={54}
            avatar={userService.userInfo?.avatar}
            name={username}
          />
          <div className={cls(styles.info)}>
            <div className={styles.label}>{t('dashboard.accountPage.email')}</div>
            <div className={styles.value}>{email}</div>
          </div>
          <div className={cls(styles.info)}>
            <div className={styles.label}>
              <span>{t('dashboard.accountPage.username')}</span>
              <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={handleChangeUsername} data-testid='account-change-username-btn'>
                <AIcon width={14} height={14} type='icon-line-global_add1' />
              </span>
            </div>
            <div className={styles.value}>{username}</div>
          </div>
          <div className={cls(styles.info)}>
            <div className={styles.label}>
              <span>{t('dashboard.accountPage.password')}</span>
              <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={handleChangePassword} data-testid='account-change-password-btn'>
                <AIcon width={14} height={14} type='icon-line-global_add1' />
              </span>
            </div>
            {userService.userInfo?.hasPassword ? (
              <div className={styles.value}>**********</div>
            ) : (
              <div className={styles.value}>
                {t('dashboard.accountPage.setPasswordToEnableEmailLogin')}
              </div>
            )}
          </div>

          <div className={cls(styles.info)}>
            <div className={styles.label}>{t('dashboard.accountPage.uid')}</div>
            <div className={styles.value}>{userService.userInfo?.uid}</div>
          </div>
          <div className={styles.info}>
            <div className={styles.label}>
              {t('dashboard.accountPage.contactAndReceiveNotifications')}:{' '}
              <ASwitch
                testId='account-notification-switch'
                style={{ marginLeft: 4 }}
                size='small'
                checked={userService.userInfo?.receiveNotification}
                onChange={handleNotificationChange}
              />
            </div>
            <div className={styles.value}>
              {t('dashboard.accountPage.receiveEmailsAboutAdeffLatestUpdatesWithThisEmail')}
            </div>
          </div>
        </div>
        <Divider className={styles.line} />
        <div className={styles.memberBox}>
          <div className={styles.memberHeader}>
            <div className={styles.left}>
              <span>
                {userService.userInfo?.corpName} ({data?.seatInfo.boundCount} /{' '}
                {data?.seatInfo.baseSeatCount})
              </span>
              {hasRequest && (
                <div className={styles.requestNotify}>{t('dashboard.accountPage.newRequest')}</div>
              )}
              {
                userService.userInfo?.isAdmin && (
                  <span style={{ marginLeft: 6, cursor: 'pointer' }} onClick={handleChangeCorpName} data-testid='account-change-corp-name-btn'>
                    <AIcon width={16} height={16} type='icon-line-global_add1' />
                  </span>
                )
              }
            </div>
            <div className={styles.right}>
              {userService.userInfo?.isAdmin &&
                <AButton testId='account-check-invitation-code-btn' shape='round' onClick={handleCheckInvitationCode}>
                  <AIcon type='icon-line-global_add1'></AIcon>
                  <span>{t('dashboard.accountPage.invitationCode')}</span>
                </AButton>
              }
              {userService.userInfo?.isAdmin && (
                <>
                  <Divider style={{ marginLeft: 16, marginRight: 20 }} type='vertical' />
                  <AButton
                    testId='account-add-member-btn'
                    size='SM'
                    type='primary'
                    shape='round'
                    onClick={showAddMemberToWorkspaceModal}
                  >
                    <AIcon type='icon-line-global_add'></AIcon>
                    <span>{t('dashboard.accountPage.message.member')}</span>
                  </AButton>
                </>
              )}
            </div>
          </div>
          <div className={styles.memberContent}>
            <div className={styles.memberTable}>
              <div className={styles.tableHeader}>
                <div className={styles.headerItem}>{t('dashboard.accountPage.table.userName')}</div>
                <div className={styles.headerItem}>{t('dashboard.accountPage.table.email')}</div>
                <div className={styles.headerItem}>
                  {t('dashboard.accountPage.table.operation')}
                </div>
              </div>
              <div className={styles.tableContent}>
                {memberList.map((member) => {
                  return (
                    <div className={styles.member} key={member.id}>
                      <div className={styles.info}>
                        <span className={cls(styles.memberAvatarBox, styles[member.status])}>
                          <DefaultAvatar
                            avatar={member.avatar}
                            name={member.name}
                          />
                        </span>
                        <span className={styles.name}>{member.name}</span>
                        {member.isAdmin && (
                          <span className={styles.role}>
                            <MiddleText text={t('dashboard.accountPage.table.admin')} />
                          </span>
                        )}
                      </div>
                      <div className={styles.email}>{member.email}</div>
                      <div className={styles.action}>
                        {member.status === 'bound' && (
                          <AButton
                            testId={`account-remove-member-btn-${member.id}`}
                            size='XS'
                            disabled={member.isAdmin || !userService.userInfo?.isAdmin}
                            style={
                              member.isAdmin || !userService.userInfo?.isAdmin
                                ? {}
                                : { borderColor: '#C7C7FF', color: '#6D54FA', fontWeight: 400 }
                            }
                            shape='round'
                            type='outlined'
                            onClick={() => showRemoveMemberModal(member)}
                          >
                            <MiddleText text={t('dashboard.accountPage.table.remove')} />
                          </AButton>
                        )}
                        {member.status === 'applying' && (
                          <div style={{ display: 'flex', columnGap: 12 }}>
                            <AButton
                              testId={`account-deny-member-request-btn-${member.id}`}
                              size='XS'
                              shape='round'
                              type='outlined'
                              style={{ borderColor: '#C7C7FF', color: '#6D54FA', fontWeight: 400 }}
                              onClick={() => {
                                handleProcessStaffRequest(member, 'deny')
                              }}
                            >
                              {t('dashboard.accountPage.table.deny')}
                            </AButton>
                            <AButton
                              testId={`account-approve-member-request-btn-${member.id}`}
                              size='XS'
                              shape='round'
                              type='primary'
                              style={{ background: '#6E79F7', color: '#fff', fontWeight: 400 }}
                              onClick={() => {
                                handleProcessStaffRequest(member, 'approve')
                              }}
                            >
                              {t('dashboard.accountPage.table.approve')}
                            </AButton>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
                {loading && (
                  <div className={styles.loadingCover}>
                    <Spin />
                  </div>
                )}
              </div>
            </div>
            {total !== undefined && total > pageSize && (
              <APagination
                testId='account-member-pagination'
                style={{ marginTop: 16 }}
                align='end'
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default observer(AccountPage)
